import { useMemo, useState, useCallback, useEffect } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  Handle,
  Position,
  ReactFlowProvider,
  useReactFlow,
} from "@xyflow/react";
import dagre from "dagre";

import "@xyflow/react/dist/style.css";
import styles from "./DecisionGraph.module.css";
import { convertToUserTimezone } from "../../../../../utils/dateService";

// ─── Condition formatting ──────────────────────────────────────────
//
// AST → human-readable expression string used on edge labels and in
// the inspector's "Condition" code block. Infix operators render as
// `a < b`; everything else (e.g. `in`, `not`) renders as `op(a, b)`.
// Boolean groupings (`and`, `or`) are parenthesised so nested edges
// stay unambiguous on the small space of an edge label.

const INFIX_OPS = new Set(["<", "<=", ">", ">=", "==", "!=", "and", "or"]);

export function formatCondition(node) {
  if (!node) return "";
  switch (node.node_type) {
    case "operator": {
      const parts = (node.operands ?? []).map(formatCondition);
      if (INFIX_OPS.has(node.op)) {
        const joined = parts.join(` ${node.op} `);
        return node.op === "and" || node.op === "or" ? `(${joined})` : joined;
      }
      return `${node.op}(${parts.join(", ")})`;
    }
    case "feature_ref":
      return node.name;
    case "literal":
      return typeof node.value === "string"
        ? `"${node.value}"`
        : String(node.value);
    default:
      return node.id ?? "?";
  }
}

// SVG `<text>` (which React Flow uses for edge labels) has no
// auto-wrap, so a fully-expanded condition like
// `(withdrawals_per_day_7d > 5 and not(is_vip == true))` paints as one
// long horizontal string that collides with sibling edge labels coming
// out of the same decision node. `summarizeCondition` keeps the label
// short and predictable: if the full expression fits in `max` chars we
// keep it; otherwise we collapse to the top-level operator + arity
// (`and(2)`, `or(3)`, `not(1)`). The full AST is still available in
// the inspector — this is just the canvas-side teaser.
export function summarizeCondition(node, max = 36) {
  if (!node) return "";
  const full = formatCondition(node);
  if (full.length <= max) return full;
  if (node.node_type === "operator") {
    const arity = node.operands?.length ?? 0;
    return `${node.op}(${arity})`;
  }
  return `${full.slice(0, max - 1)}…`;
}

// ─── DAG flattening ────────────────────────────────────────────────
//
// Walks the BE-shaped `nodes` map and produces React Flow's `{nodes,
// edges}` arrays. Conditional edges carry the AST under `data.condition`
// so the inspector can render the full tree on click. The `default_target`
// fallback becomes its own edge with `label: "else"` and a dashed stroke
// so it reads as "everything else" without overlapping the labelled
// edges visually.
//
// Only nodes reachable from `source_node_id` are emitted. The mapper
// always writes the full 7-node scaffold (both tiers + all three
// terminals) for stable version diffs, so a "no rules enabled" graph
// still carries `t2_src`, `t2_decision`, `auto_approve`, and
// `human_review` in the payload — but nothing routes to them at
// runtime, and drawing them on the canvas would falsely imply extra
// possible outcomes. Filtering here (rather than in the mapper)
// keeps the published JSON canonical while letting the visualisation
// mirror what actually executes.

/**
 * BFS from `source_node_id` collecting every node id transitively
 * reachable through source `next_node_id` transitions and decision
 * `edges[].target` / `default_target` transitions. Terminals have no
 * outgoing links so the walk stops there naturally. Returns an empty
 * set when the entry node is missing — treated the same as an empty
 * graph downstream.
 */
function reachableFromSource(graph) {
  const reachable = new Set();
  const start = graph.source_node_id;
  if (!start || !graph.nodes?.[start]) return reachable;

  const queue = [start];
  while (queue.length > 0) {
    const id = queue.shift();
    if (reachable.has(id)) continue;
    reachable.add(id);
    const node = graph.nodes[id];
    if (!node) continue;
    if (node.node_type === "source" && node.next_node_id) {
      queue.push(node.next_node_id);
    }
    if (node.node_type === "decision") {
      for (const e of node.edges ?? []) {
        if (e?.target) queue.push(e.target);
      }
      if (node.default_target) queue.push(node.default_target);
    }
  }
  return reachable;
}

export function toFlow(graph) {
  const nodes = [];
  const edges = [];
  if (!graph?.nodes) return { nodes, edges };

  const reachable = reachableFromSource(graph);

  for (const node of Object.values(graph.nodes)) {
    if (!reachable.has(node.id)) continue;
    nodes.push({
      id: node.id,
      type: node.node_type,
      position: { x: 0, y: 0 },
      data: { node, isSource: node.id === graph.source_node_id },
      selectable: false,
      draggable: false,
      focusable: false,
      connectable: false,
    });

    if (node.node_type === "source" && node.next_node_id) {
      edges.push({
        id: `${node.id}->${node.next_node_id}`,
        source: node.id,
        target: node.next_node_id,
      });
    }

    if (node.node_type === "decision") {
      for (const e of node.edges ?? []) {
        edges.push({
          id: `${node.id}->${e.target}:${e.condition?.id ?? "c"}`,
          source: node.id,
          target: e.target,
          // `label` is what paints on the canvas — keep it short to
          // avoid sibling-label collision near multi-branch decisions
          // (e.g. t2_decision fans out into 3 edges). The untruncated
          // expression lives in `data.fullLabel` for the inspector.
          label: summarizeCondition(e.condition),
          data: {
            kind: "conditional",
            condition: e.condition,
            fullLabel: formatCondition(e.condition),
          },
        });
      }
      if (node.default_target) {
        edges.push({
          id: `${node.id}->${node.default_target}:default`,
          source: node.id,
          target: node.default_target,
          label: "else",
          data: { kind: "default" },
          animated: false,
          style: { strokeDasharray: "5 4", stroke: "#94a3b8" },
          labelStyle: { fill: "#64748b", fontStyle: "italic" },
        });
      }
    }
  }
  return { nodes, edges };
}

// ─── Auto-layout (dagre, left → right) ─────────────────────────────
//
// NODE_W/NODE_H are duplicated in `DecisionGraph.module.css` via
// `--node-w` / `--node-h` so visual width matches the boxes dagre
// reserves. Keep both in sync on resize.

const NODE_W = 200;
const NODE_H = 64;

export function layout(nodes, edges) {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({
    rankdir: "LR",
    // Semantics under `LR`:
    //   nodesep = *vertical* gap between siblings on the same rank
    //             (i.e. multiple fan-out branches of one decision).
    //   ranksep = *horizontal* gap between successive ranks
    //             (source → decision → terminal, tier 1 → tier 2).
    // Ranksep must clear the widest edge label (`summarizeCondition`
    // caps at 36 chars × ~5px monospace ≈ 180px) plus breathing room
    // so the label doesn't collide with the adjacent node's border.
    // Nodesep is sized to keep fan-out branches (a decision's edges
    // + its default_target) from crowding into each other visually.
    nodesep: 100,
    ranksep: 220,
    marginx: 24,
    marginy: 24,
  });

  nodes.forEach((n) => g.setNode(n.id, { width: NODE_W, height: NODE_H }));
  edges.forEach((e) => g.setEdge(e.source, e.target));
  dagre.layout(g);

  const positioned = nodes.map((n) => {
    const { x, y } = g.node(n.id);
    return {
      ...n,
      position: { x: x - NODE_W / 2, y: y - NODE_H / 2 },
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
    };
  });
  return { nodes: positioned, edges };
}

// ─── Custom node components ────────────────────────────────────────

const TERMINAL_STYLE = {
  auto_approve: {
    bg: "#ecfdf5",
    border: "#10b981",
    text: "#065f46",
    label: "Auto-approve",
  },
  human_review: {
    bg: "#fffbeb",
    border: "#f59e0b",
    text: "#92400e",
    label: "Human review",
  },
  auto_reject: {
    bg: "#fef2f2",
    border: "#ef4444",
    text: "#991b1b",
    label: "Auto-reject",
  },
  // `processed` = "T1 did not fire, no rule opinion". Operationally
  // identical to auto_approve but analytics-distinct — a cool blue
  // palette signals "passthrough" without competing with the green
  // (active approval) or amber (needs review) branches.
  processed: {
    bg: "#eff6ff",
    border: "#3b82f6",
    text: "#1e40af",
    label: "Processed",
  },
};

function TierBadge({ tier }) {
  if (!tier) return null;
  return <span className={styles.tier}>{tier.replace("tier_", "T")}</span>;
}

function SourceNode({ data }) {
  return (
    <div
      className={`${styles.node} ${styles.source}`}
      data-testid={`decision-graph-node-${data.node.id}`}
      data-node-type="source"
    >
      {/* Source nodes are the entry of a tier (e.g. `t1_src`) but they
       * can *also* be the target of a cross-tier `default_target` —
       * e.g. `t1_decision`'s "else" edge routes into `t2_src`. Without
       * this target handle the incoming edge has no endpoint to land
       * on, React Flow silently drops the path, and the link between
       * tiers disappears from the canvas. */}
      <Handle type="target" position={Position.Left} />
      <div className={styles.nodeHead}>
        <span className={styles.nodeKind}>SOURCE</span>
        <TierBadge tier={data.node.tier} />
      </div>
      <div className={styles.nodeId}>{data.node.id}</div>
      <Handle type="source" position={Position.Right} />
    </div>
  );
}

function DecisionNode({ data }) {
  const branchCount =
    (data.node.edges?.length ?? 0) + (data.node.default_target ? 1 : 0);
  return (
    <div
      className={`${styles.node} ${styles.decision}`}
      data-testid={`decision-graph-node-${data.node.id}`}
      data-node-type="decision"
    >
      <Handle type="target" position={Position.Left} />
      <div className={styles.nodeHead}>
        <span className={styles.nodeKind}>DECISION</span>
        <TierBadge tier={data.node.tier} />
      </div>
      <div className={styles.nodeId}>{data.node.id}</div>
      <div className={styles.nodeSub}>{branchCount} branches</div>
      <Handle type="source" position={Position.Right} />
    </div>
  );
}

function TerminalNode({ data }) {
  const decision = data.node.decision;
  const palette = TERMINAL_STYLE[decision] ?? {
    bg: "#f8fafc",
    border: "#64748b",
    text: "#334155",
    label: decision,
  };
  return (
    <div
      className={`${styles.node} ${styles.terminal}`}
      style={{
        background: palette.bg,
        borderColor: palette.border,
        color: palette.text,
      }}
      data-testid={`decision-graph-node-${data.node.id}`}
      data-node-type="terminal"
      data-decision={decision}
    >
      <Handle type="target" position={Position.Left} />
      <div className={styles.nodeHead}>
        <span className={styles.nodeKind} style={{ color: palette.text }}>
          TERMINAL
        </span>
      </div>
      <div className={styles.terminalDecision}>{palette.label}</div>
    </div>
  );
}

const nodeTypes = {
  source: SourceNode,
  decision: DecisionNode,
  terminal: TerminalNode,
};

// ─── Condition inspector ──────────────────────────────────────────

// Exported so unit tests can render each of its three branches
// (operator / feature_ref / literal) in isolation without having to
// drive the inspector open through a mocked React Flow edge click.
// The component is intentionally minimal — no state, no context —
// so exporting it is cost-free.
export function ConditionTree({ node, depth = 0 }) {
  if (!node) return null;
  // Indent goes on the *wrapper*, not on the row itself. If we shoved
  // `paddingLeft` onto a flex row with `min-width: 0`, the padding
  // would inflate the row's intrinsic min-width and defeat the
  // wrapping rules in `.treeFeature` — deep nodes would push past the
  // panel edge again. Wrapping the row keeps the flex container
  // unaffected by the indent.
  const indent = { marginLeft: depth * 14 };
  // `data-testid` marks the top-level wrapper of every sub-tree so
  // unit tests can assert against `marginLeft` (indent) and the
  // sub-tree's presence/absence via `screen.getByTestId` /
  // `queryByTestId` without reaching into `container.firstChild` or
  // querying by DOM class (banned by `testing-library/no-container`
  // and `testing-library/no-node-access`).
  if (node.node_type === "operator") {
    return (
      <div data-testid="condition-tree-node" style={indent}>
        <div className={styles.treeOp}>
          <span>{node.op}</span>
          <span className={styles.treeId}>#{node.id}</span>
        </div>
        {node.operands?.map((o) => (
          <ConditionTree key={o.id} node={o} depth={depth + 1} />
        ))}
      </div>
    );
  }
  return (
    <div
      data-testid="condition-tree-node"
      className={styles.treeLeaf}
      style={indent}
    >
      {node.node_type === "feature_ref" ? (
        <span className={styles.treeFeature}>{node.name}</span>
      ) : (
        <span className={styles.treeLiteral}>{JSON.stringify(node.value)}</span>
      )}
      <span className={styles.treeId}>#{node.id}</span>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────

function GraphInner({ graph, ariaLabel }) {
  const { fitView } = useReactFlow();
  const [selectedEdge, setSelectedEdge] = useState(null);

  const { nodes, edges } = useMemo(() => {
    const flat = toFlow(graph);
    return layout(flat.nodes, flat.edges);
  }, [graph]);

  // Re-fit after the layout pass settles. The `setTimeout(…, 0)` lets
  // React Flow measure its viewport before we ask it to fit — otherwise
  // the first paint zooms to a stale (zero-sized) bbox and clips nodes.
  useEffect(() => {
    const t = setTimeout(() => fitView({ padding: 0.2 }), 0);
    return () => clearTimeout(t);
  }, [nodes, edges, fitView]);

  const onEdgeClick = useCallback((_, edge) => {
    setSelectedEdge(edge.data?.condition ? edge : null);
  }, []);

  const closeInspector = useCallback(() => setSelectedEdge(null), []);

  // `nodes` is already pruned to the reachable set by `toFlow`, so
  // this count matches what the admin sees on the canvas rather than
  // the raw payload — important once `t2_*` / terminal orphans get
  // filtered out for a "no rules enabled" version.
  const nodeCount = nodes.length;
  const versionLabel = graph?.version?.slice(0, 16) ?? "—";
  const createdAtLabel = graph?.created_at
    ? convertToUserTimezone(new Date(graph.created_at))
    : null;

  return (
    <div className={styles.root} data-testid="decision-graph">
      <header className={styles.header}>
        <div>
          <p className={styles.meta}>
            {nodeCount} nodes &middot; entry: {" "}
            <code>{graph?.source_node_id ?? "—"}</code>
          </p>
        </div>
        <div className={styles.version} title={graph?.created_by ?? undefined}>
          <span data-testid="decision-graph-version">v {versionLabel}</span>
          {createdAtLabel && (
            <span className={styles.versionDate}>{createdAtLabel}</span>
          )}
        </div>
      </header>

      <div
        className={styles.canvasWrap}
        role="application"
        aria-label={ariaLabel}
        data-testid="decision-graph-canvas"
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onEdgeClick={onEdgeClick}
          fitView
          proOptions={{ hideAttribution: true }}
          // Read-only viewer. v12 renamed `nodesSelectable` → `elementsSelectable`
          // (the per-node flags in `toFlow` are still required for the
          // children, but these globals stop React Flow from registering
          // any selection/drag/focus *event listeners* on the pane in the
          // first place). `panOnDrag` stays explicit so the user can still
          // pan the viewport by dragging the empty canvas.
          nodesDraggable={false}
          nodesConnectable={false}
          nodesFocusable={false}
          elementsSelectable={false}
          selectionOnDrag={false}
          panOnDrag
          defaultEdgeOptions={{
            labelBgPadding: [6, 3],
            // Edge labels sit on top of the dark canvas, so the label
            // pill stays semi-opaque dark with a light foreground for
            // legibility. `labelStyle` colour is honoured by React Flow
            // as the SVG text fill.
            labelBgStyle: { fill: "#0f172a", fillOpacity: 0.85 },
            labelStyle: {
              fontSize: 12,
              fontFamily: "ui-monospace, monospace",
              fill: "#e2e8f0",
            },
            style: { stroke: "#94a3b8", strokeWidth: 1.5 },
          }}
        >

          <Background gap={20} size={1.6} color="#52525e" />
          <Controls showInteractive={false} />
        </ReactFlow>

        {selectedEdge && (
          <aside
            className={styles.inspector}
            role="dialog"
            aria-label="Edge condition"
            data-testid="decision-graph-inspector"
          >
            <div className={styles.inspectorHead}>
              <span>Condition</span>
              <button
                type="button"
                className={styles.inspectorClose}
                onClick={closeInspector}
                aria-label="Close condition inspector"
              >
                ×
              </button>
            </div>
            <code
              className={styles.inspectorExpr}
              data-testid="decision-graph-inspector-expr"
            >
              {formatCondition(selectedEdge.data.condition)}
            </code>
            <div className={styles.inspectorTree}>
              <ConditionTree node={selectedEdge.data.condition} />
            </div>
            <div className={styles.inspectorRoute}>
              <span>{selectedEdge.source}</span>
              <span className={styles.routeArrow} aria-hidden="true">
                →
              </span>
              <span className={styles.routeTo}>{selectedEdge.target}</span>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}

export default function DecisionGraph({
  graph,
  ariaLabel = "Decision graph canvas",
}) {
  return (
    <ReactFlowProvider>
      <GraphInner graph={graph} ariaLabel={ariaLabel} />
    </ReactFlowProvider>
  );
}
